/**
 * Offscreen label rasterization (ADR 0045) — renders a (variable-resolved)
 * label document to raw RGBA pixels at printer DPI, using the SAME drawers
 * the editor canvas records (preview === print).
 *
 * Uses a CPU-backed `Skia.Surface.Make` (Unpremul RGBA_8888) — no on-screen
 * compositing involved, which also sidesteps the known Android-web
 * present-path issue. On web this must only run after `loadSkia()` resolves.
 *
 * Compiler-excluded (`services/`).
 *
 * @module services/labelTemplates/renderOffscreen
 * @category Services
 */

import type { SkSurface } from '@shopify/react-native-skia';
import { AlphaType, ColorType, Skia } from '@shopify/react-native-skia';
import type { LabelTemplateDocument } from '@fuze-store/shared';

import {
  dotsPerLabelUnit,
  resolveLabelSafeArea,
} from '@helpers/printing/label-template';

import { resolveLabelDocument } from './binding';
import { makeLabelFont } from './fonts';
import { registerLabelRenderers, setLabelRenderContext } from './renderers';

import type { LabelVariableContext } from '@fuze-store/print-engine/label';
import { thresholdRgbaTo1bpp } from '@fuze-store/print-engine/label';
import { LOD } from '@src/floorplan/models/camera';
import type { FloorObject } from '@src/floorplan/models/object';
import { resolveRenderer } from '@src/floorplan/renderer/RendererRegistry';
import type { FloorTheme, RenderContext } from '@src/floorplan/renderer/types';

export interface LabelRasterResult {
  rgba: Uint8Array;
  widthDots: number;
  heightDots: number;
}

/** Monochrome print theme — content drawers force black ink anyway. */
const PRINT_THEME: FloorTheme = {
  surface: '#ffffff',
  grid: '#ffffff',
  objectFill: '#ffffff',
  objectStroke: '#000000',
  wall: '#000000',
  label: '#000000',
  accent: '#000000',
  selection: '#000000',
};

/**
 * Canvas transform (in world-unit space, applied AFTER the dpi scale) that fits
 * the full design canvas into the printable safe rectangle — a uniform scale
 * (QR/barcode stay square) + centering. Content designed edge-to-edge is nudged
 * inward instead of being clipped at the label border. The editor's on-canvas
 * safe-area guide reads the SAME `resolveLabelSafeArea` rectangle, so the design
 * surface stays WYSIWYG with what actually prints.
 */
const applySafeAreaTransform = (
  canvas: ReturnType<SkSurface['getCanvas']>,
  doc: LabelTemplateDocument,
): void => {
  const W = doc.engineDoc.width;
  const H = doc.engineDoc.height;
  const safe = resolveLabelSafeArea(doc.media, W, H);
  const s = Math.min(safe.width / W, safe.height / H, 1);
  if (s >= 1 && safe.x === 0 && safe.y === 0) return;
  const tx = safe.x + (safe.width - W * s) / 2;
  const ty = safe.y + (safe.height - H * s) / 2;
  canvas.translate(tx, ty);
  canvas.scale(s, s);
};

const renderOrder = (objects: readonly FloorObject[]): FloorObject[] =>
  [...objects]
    .filter((o) => o.visible !== false)
    .sort((a, b) => a.zIndex - b.zIndex);

const makeSurface = (w: number, h: number): SkSurface => {
  const cpu = Skia.Surface.Make(w, h);
  if (cpu !== null) return cpu;
  const gpu = Skia.Surface.MakeOffscreen(w, h);
  if (gpu !== null) return gpu;
  throw new Error(`labelTemplates: could not create ${w}x${h} surface`);
};

/**
 * Render an ALREADY variable-resolved document to raw RGBA at printer dots.
 * `dpiOverride` re-targets the raster density (e.g. the routed printer's
 * media dpi differing from the template's design dpi).
 */
export const renderLabelDocumentToRgba = async (
  doc: LabelTemplateDocument,
  opts: { dpiOverride?: number } = {},
): Promise<LabelRasterResult> => {
  registerLabelRenderers();
  const dpi = opts.dpiOverride ?? doc.media.dpi;
  const scale = dotsPerLabelUnit(dpi);
  const widthDots = Math.max(1, Math.round(doc.engineDoc.width * scale));
  const heightDots = Math.max(1, Math.round(doc.engineDoc.height * scale));

  const font = await makeLabelFont();
  const surface = makeSurface(widthDots, heightDots);
  const canvas = surface.getCanvas();
  canvas.clear(Skia.Color('#ffffff'));
  canvas.scale(scale, scale);
  applySafeAreaTransform(canvas, doc);

  const ctx: RenderContext = { lod: LOD.CLOSE, theme: PRINT_THEME, font };
  const objects = renderOrder(doc.engineDoc.objects as FloorObject[]);
  for (const object of objects) {
    resolveRenderer(object.type).draw(canvas, object, ctx);
  }
  surface.flush();

  const snapshot = surface.makeImageSnapshot();
  const pixels = snapshot.readPixels(0, 0, {
    width: widthDots,
    height: heightDots,
    colorType: ColorType.RGBA_8888,
    alphaType: AlphaType.Unpremul,
  });
  if (pixels === null) {
    throw new Error('labelTemplates: readPixels returned null');
  }
  const rgba =
    pixels instanceof Uint8Array
      ? pixels
      : Uint8Array.from(pixels as Float32Array);
  return { rgba, widthDots, heightDots };
};

/**
 * Threshold-first PNG preview (data URI) — shows what the PRINTER sees
 * (true 1-bit), doubling as an ongoing raster-path validation surface.
 * Renders variables from `ctx` (callers pass the SAMPLE context at design
 * time, the real one on print dialogs).
 */
export const renderLabelPreviewPng = async (
  doc: LabelTemplateDocument,
  ctx: LabelVariableContext,
  opts: { forPrint?: boolean } = {},
): Promise<string> => {
  const resolved = resolveLabelDocument(doc, ctx);
  setLabelRenderContext(ctx, opts.forPrint ?? true);
  const { rgba, widthDots, heightDots } =
    await renderLabelDocumentToRgba(resolved);
  setLabelRenderContext(ctx, false);

  const mono = thresholdRgbaTo1bpp(rgba, widthDots, heightDots);
  // Rebuild a black/white RGBA from the 1bpp bitmap so the preview is the
  // exact print image, then encode to PNG.
  const bw = new Uint8Array(widthDots * heightDots * 4);
  for (let y = 0; y < heightDots; y++) {
    const rowBase = y * mono.bytesPerRow;
    for (let x = 0; x < widthDots; x++) {
      const black =
        (mono.bitmap[rowBase + (x >> 3)]! & (0x80 >> (x & 7))) !== 0;
      const i = (y * widthDots + x) * 4;
      const v = black ? 0 : 255;
      bw[i] = v;
      bw[i + 1] = v;
      bw[i + 2] = v;
      bw[i + 3] = 255;
    }
  }
  const data = Skia.Data.fromBytes(bw);
  const image = Skia.Image.MakeImage(
    {
      width: widthDots,
      height: heightDots,
      colorType: ColorType.RGBA_8888,
      alphaType: AlphaType.Unpremul,
    },
    data,
    widthDots * 4,
  );
  if (image === null) {
    throw new Error('labelTemplates: MakeImage failed for preview');
  }
  const base64 = image.encodeToBase64();
  return `data:image/png;base64,${base64}`;
};
